// Global chart cache service
const chartCache = new Map(); // { [cacheKey]: { data, lastFetched } }

// Cache cleanup function to prevent memory leaks
function cleanupOldCacheEntries() {
  const now = Date.now();
  const maxAge = 24 * 60 * 60 * 1000; // 24 hours
  
  for (const [key, value] of chartCache.entries()) {
    if (now - value.lastFetched > maxAge) {
      chartCache.delete(key);
    }
  }
}

// Clean up old entries every hour
setInterval(cleanupOldCacheEntries, 60 * 60 * 1000);

// Helper: generate cache key
function getCacheKey(symbol, range, points = null) {
  const baseKey = `${symbol.toLowerCase()}:${range}`;
  return points ? `${baseKey}:${points}` : baseKey;
}

// Main cache function
export async function getCachedChart(symbol, range, points = null, refreshMinutes = 10) {
  const cacheKey = getCacheKey(symbol, range, points);
  const now = Date.now();
  
  // Check cache first
  const cached = chartCache.get(cacheKey);
  if (cached && now - cached.lastFetched < refreshMinutes * 60 * 1000) {
    return cached.data;
  }

  // Fetch new data
  try {
    const api = (await import('../services/api')).default;
    let url = `/api/coins/${symbol}/chart?range=${range}`;
    if (points) {
      url += `&points=${points}&optimize=true`;
    }
    
    const res = await api.get(url);
    const data = res.data;
    
    // Store in cache
    chartCache.set(cacheKey, { data, lastFetched: Date.now() });
    
    return data;
  } catch (error) {
    console.error(`[ChartCache] Error fetching ${symbol}:`, error.message);
    // On error, return last cached data if available
    if (cached) {
      return cached.data;
    }
    throw error;
  }
}

// Debug function to check cache status
export function debugChartCache() {
  console.log('[ChartCache] Cache status:', {
    totalEntries: chartCache.size,
    cachedKeys: Array.from(chartCache.keys()),
    cacheSize: chartCache.size
  });
}

// Clear cache for specific symbol
export function clearChartCache(symbol = null) {
  if (symbol) {
    const symbolLower = symbol.toLowerCase();
    for (const [key] of chartCache.entries()) {
      if (key.startsWith(symbolLower + ':')) {
        chartCache.delete(key);
      }
    }
  } else {
    chartCache.clear();
  }
}

// Export cache for direct access if needed
export { chartCache }; 